import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import { useCallback, useEffect, useState } from "react";

import { api } from "./client";

// Passkeys / WebAuthn. Registration adds an authenticator to the signed-in
// account; authentication is usernameless (a discoverable credential signs the
// user in). The server issues a one-time `challenge_id` we echo back on verify.

export const passkeysSupported = (): boolean => {
  // Guarded: the check touches `window`/`navigator` APIs that can be absent or
  // throw outside a real browser (e.g. jsdom under test).
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
};

export type Passkey = {
  readonly created_at: string;
  readonly label: string;
  readonly last_used_at: string | null;
  readonly public_id: string;
};

type RegOptions = Parameters<typeof startRegistration>[0]["optionsJSON"];
type AuthOptions = Parameters<typeof startAuthentication>[0]["optionsJSON"];

export const listPasskeys = (): Promise<ReadonlyArray<Passkey>> =>
  api.get<ReadonlyArray<Passkey>>("/webauthn/credentials");

export const usePasskeys = (): {
  loading: boolean;
  passkeys: ReadonlyArray<Passkey>;
  refetch: () => void;
} => {
  const [passkeys, setPasskeys] = useState<ReadonlyArray<Passkey>>([]);
  const [loading, setLoading] = useState(true);
  const refetch = useCallback(() => {
    void listPasskeys()
      .then((rows) => setPasskeys(Array.isArray(rows) ? rows : []))
      .catch(() => setPasskeys([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => refetch(), [refetch]);
  return { loading, passkeys, refetch };
};

// A user who cancels or dismisses the platform prompt surfaces a
// `NotAllowedError` — that isn't an error worth showing.
export const isPasskeyCancellation = (err: unknown): boolean =>
  err instanceof Error && err.name === "NotAllowedError";

export const removePasskey = (id: string): Promise<void> =>
  api.del<void>(`/webauthn/credentials/${id}`);

export const registerPasskey = async (
  label: string,
): Promise<{ label: string }> => {
  const start = await api.post<{ challenge_id: string; options: RegOptions }>(
    "/webauthn/register/options",
  );
  const response = await startRegistration({ optionsJSON: start.options });
  const done = await api.post<{ credential: { label: string } }>(
    "/webauthn/register/verify",
    {
      challenge_id: start.challenge_id,
      label,
      response,
    },
  );
  return done.credential;
};

export const authenticateWithPasskey = async (): Promise<{
  public_id: string;
}> => {
  const start = await api.post<{ challenge_id: string; options: AuthOptions }>(
    "/webauthn/authenticate/options",
  );
  const response = await startAuthentication({ optionsJSON: start.options });
  const done = await api.post<{ user: { public_id: string } }>(
    "/webauthn/authenticate/verify",
    {
      challenge_id: start.challenge_id,
      response,
    },
  );
  return done.user;
};

// Enrollment: mint a passkey for a not-yet-existing user via a link token. The
// registration ceremony creates the account + session server-side.
export const enrollWithPasskey = async (
  token: string,
  displayName: string,
): Promise<{ public_id: string }> => {
  const start = await api.post<{ challenge_id: string; options: RegOptions }>(
    "/webauthn/enroll/options",
    { token },
  );
  const response = await startRegistration({ optionsJSON: start.options });
  const done = await api.post<{ workspace: { public_id: string } }>(
    "/webauthn/enroll/verify",
    {
      challenge_id: start.challenge_id,
      display_name: displayName,
      response,
      token,
    },
  );
  return done.workspace;
};
